import type { PhysicsStats } from '../physics/types';

export type AvbdRuntimeOverlayState = {
  substeps: number;
  solverIterations: number;
  alpha: number;
  beta: number;
  gamma: number;
  kStart: number;
};

export class DebugOverlay {
  private infoDiv: HTMLDivElement;
  private metricsPre: HTMLPreElement;
  private startTimeMs: number;

  constructor() {
    this.startTimeMs = performance.now();

    this.infoDiv = document.createElement('div');
    this.infoDiv.dataset.testid = 'avbd-runtime-card';
    Object.assign(this.infoDiv.style, {
      position: 'absolute',
      bottom: '10px',
      left: '10px',
      color: '#f8fafc',
      fontFamily: 'Inter, ui-monospace, SFMono-Regular, Consolas, monospace',
      fontSize: '12px',
      background: 'linear-gradient(145deg, rgba(8, 12, 20, 0.93), rgba(13, 24, 33, 0.88))',
      border: '1px solid rgba(104, 211, 184, 0.28)',
      boxShadow: '0 18px 50px rgba(0, 0, 0, 0.35)',
      padding: '11px 13px',
      borderRadius: '10px',
      lineHeight: '1.45',
      backdropFilter: 'blur(12px)',
      zIndex: '9',
    });

    const heading = document.createElement('div');
    heading.textContent = 'PMabdp · WebGPU AVBD';
    Object.assign(heading.style, {
      color: '#9ff7df',
      fontFamily: 'system-ui, sans-serif',
      fontWeight: '750',
      fontSize: '13px',
      letterSpacing: '0.02em',
      marginBottom: '5px',
    });

    this.metricsPre = document.createElement('pre');
    this.metricsPre.dataset.testid = 'avbd-runtime-stats';
    Object.assign(this.metricsPre.style, {
      margin: '0',
      font: 'inherit',
      whiteSpace: 'pre',
    });

    const links = document.createElement('div');
    Object.assign(links.style, {
      display: 'flex',
      gap: '10px',
      marginTop: '7px',
      fontFamily: 'system-ui, sans-serif',
      fontSize: '11px',
    });
    links.append(
      this.makeLink('运行 GPU 验证', './avbd-regression.html', 'verification-link'),
      this.makeLink(
        '论文',
        'https://graphics.cs.utah.edu/research/projects/avbd/Augmented_VBD-SIGGRAPH25.pdf',
        'paper-link',
      ),
    );

    this.infoDiv.append(heading, this.metricsPre, links);
    document.body.appendChild(this.infoDiv);
  }

  private makeLink(label: string, href: string, testId: string): HTMLAnchorElement {
    const link = document.createElement('a');
    link.textContent = label;
    link.href = href;
    link.dataset.testid = testId;
    link.target = '_blank';
    link.rel = 'noreferrer';
    Object.assign(link.style, {
      color: '#70e1c2',
      textDecoration: 'none',
      borderBottom: '1px solid rgba(112, 225, 194, 0.42)',
    });
    return link;
  }

  begin(): void {
    // No-op: frame timing panel removed.
  }

  end(): void {
    // No-op: frame timing panel removed.
  }

  reset(): void {
    this.startTimeMs = performance.now();
  }

  update(phys: PhysicsStats, avbd?: AvbdRuntimeOverlayState): void {
    const elapsedSeconds = (performance.now() - this.startTimeMs) / 1000;
    const lines = [
      `Bodies             ${phys.bodyCount}`,
      `Frame              ${phys.frameCount}`,
      `Elapsed            ${elapsedSeconds.toFixed(1)} s`,
    ];
    if (avbd) {
      const sweepsPerFrame = avbd.substeps * avbd.solverIterations;
      lines.push(
        `Solver iterations  ${avbd.solverIterations} × ${avbd.substeps} substep`,
        `Primal / dual      ${sweepsPerFrame} / ${sweepsPerFrame} passes`,
        `α / β / γ          ${avbd.alpha.toFixed(3)} / ${avbd.beta.toFixed(0)} / ${avbd.gamma.toFixed(3)}`,
        `k start            ${avbd.kStart.toExponential(1)}`,
      );
    }
    this.metricsPre.textContent = lines.join('\n');
  }
}
